import React from 'react';
import { CountryTemplate } from '../../components/CountryTemplate';

export const KenyaPage = () => {
  const kenyaData = {
    name: 'Kenya',
    region: 'Africa',
    color: '#606090',
    metaTitle: 'Pharma & Nutraceutical Registration in Kenya | CTD Dossiers | TheApostrophe',
    metaDescription: 'Understand how to register pharma and nutraceutical products in Kenya. CTD dossier requirements, health supplement rules, labelling, key documents, and regulatory pathways for an import-dependent East African market.',
    
    hero: {
      title: 'Pharma & Nutraceutical Registration in Kenya',
      subtitle: 'A high-potential East African hub with strong import dependence, structured CTD requirements, and growing demand for chronic therapies and health supplements.',
      cta: 'Start Your Kenya Registration',
      secondaryCta: 'Download Kenya Readiness Checklist'
    },
    
    atAGlance: [
      { label: 'Population', value: '~55M' },
      { label: 'Income Level', value: 'Lower-middle income' },
      { label: 'Health Expenditure per Capita', value: '~USD 90' },
      { label: 'Health Expenditure (% of GDP)', value: '~4.3%' },
      { label: 'Adults Overweight/Obese', value: '~1/3 of adults' },
      { label: 'India Pharma Exports to Kenya', value: '~USD 352M' }
    ],
    
    marketOverview: {
      title: 'Market Overview: An Import-Dependent Pharma Hub',
      stats: [
        'Kenya is a key pharma hub for East Africa, with most finished pharmaceuticals imported',
        'India is a leading supplier of pharmaceutical products to Kenya, exporting about US$352 million worth of pharmaceuticals in 2024',
        'Kenyan distributors often service regional markets (Uganda, Tanzania, Rwanda, South Sudan)',
        'A Kenyan registration can indirectly support broader East African access'
      ],
      opportunities: [
        'Well-documented Indian products with strong quality positioning can gain solid foothold',
        'Regional gateway to East African Community markets',
        'High acceptance of Indian dossiers and products',
        'Established importer network with regional reach'
      ]
    },
    
    diseaseburden: {
      title: 'Disease Burden: What Drives Demand in Kenya',
      subtitle: 'Kenya faces a dual burden of rising NCDs alongside persistent infectious diseases.',
      categories: [
        {
          name: 'Non-Communicable Diseases',
          items: [
            'Cardiovascular diseases - leading cause of death',
            'Cancer - third leading cause (~44,700 new cases in 2022)',
            'Diabetes and metabolic disorders - rapidly increasing',
            'Adult obesity has tripled since 2000 (~13% now obese)',
            'Portfolio fit: Cardio-metabolic products, metabolic health supplements'
          ]
        },
        {
          name: 'Infectious Diseases',
          items: [
            'TB - ~128,000 cases (223 per 100,000 population)',
            'Malaria - ~3.3 million cases in 2023',
            'Endemic in lake and coastal regions',
            'Portfolio fit: Anti-infectives, supportive therapies, paediatric formulations'
          ]
        },
        {
          name: 'Nutraceutical Opportunities',
          items: [
            'Metabolic health and weight management',
            'Cardiovascular support products',
            'Immune and recovery supplements',
            'Vitamin C+Zinc combinations',
            'Joint health and antioxidant support'
          ]
        }
      ]
    },
    
    regulatoryFramework: {
      authority: 'Pharmacy and Poisons Board (PPB)',
      description: 'Ministry of Health, Kenya. PPB regulates all medicines and many health products. Kenya participates in the East African Community (EAC) medicines regulatory harmonisation and uses a Common Technical Document (CTD) format.',
      pharma: {
        timeline: 'Varies based on dossier completeness (several months for well-prepared applications)',
        requirements: [
          'CTD-based dossier (Modules 1-5) consistent with EAC guidelines',
          'Administrative forms and certificates',
          'Product information (SmPC, PIL, labels)',
          'Quality documentation for API & finished product',
          'Non-clinical and clinical summaries',
          'Stability data suitable for local climate',
          'GMP certificate and manufacturing license',
          'CoPP or evidence of registration in country of origin'
        ],
        insight: 'Kenya requires CTD format aligned with EAC harmonisation guidelines. Queries commonly relate to stability data, specifications, bioequivalence, and product information consistency.'
      },
      nutra: {
        timeline: 'Subject to product classification and documentation completeness',
        requirements: [
          'Clear product classification (supplement vs medicine)',
          'Full formulation with active ingredients and excipients',
          'Certificate of Analysis (finished product and key ingredients)',
          'Manufacturer GMP or equivalent quality certification',
          'Evidence of safety for novel or high-dose ingredients',
          'Labelling in English or Kiswahili with required information',
          'Non-misleading claims and structure-function justification',
          'Marketing authorization from PPB required'
        ],
        insight: 'Products must be clearly distinguished from medicines (no medicinal claims if registered as supplements). Labelling must include brand name, generic names, quantity per dose, batch number, dates, storage, and recommended use.'
      }
    },
    
    pharmaPathway: {
      title: 'Pharmaceutical Registration (CTD)',
      steps: [
        'Appointment of Local Partner / Marketing Authorization Holder (MAH)',
        'CTD dossier compilation (Modules 1-5) with EAC harmonisation alignment',
        'Administrative forms and certificates preparation',
        'Product information documents (SmPC, PIL, labels)',
        'Quality documentation for API and finished product',
        'Stability data for local climate conditions',
        'Submission to PPB for evaluation',
        'Query handling (stability, specifications, bioequivalence, etc.)',
        'Marketing authorization approval and renewals'
      ]
    },
    
    nutraPathway: {
      title: 'Health Supplement & Nutraceutical Registration',
      steps: [
        'Product classification (supplement vs medicine distinction)',
        'Full formulation documentation with quantitative composition',
        'Certificate of Analysis for finished product and key ingredients',
        'GMP or quality certification for manufacturing site',
        'Safety evidence for novel or high-dose ingredients',
        'English or Kiswahili labelling with PPB compliance',
        'Claims justification for structure-function or health claims',
        'Submission to PPB for marketing authorization',
        'Registration approval and validity period (subject to renewal)'
      ]
    },
    
    requiredDocs: {
      'Pharmaceuticals (CTD)': [
        'Completed PPB application form and administrative documents',
        'CTD dossier (Modules 1-5)',
        'Manufacturing licence & GMP certificate',
        'CoPP or evidence of registration/authorization',
        'API documentation / DMF or equivalent',
        'Finished product specifications & COA',
        'Stability data for relevant climatic zone',
        'SmPC, patient information leaflet, labels (English/Kiswahili)'
      ],
      'Health Supplements / Nutraceuticals': [
        'Product classification & rationale',
        'Complete qualitative & quantitative composition',
        'COAs for finished product and key ingredients',
        'GMP / quality certification for manufacturing site',
        'Evidence of safety for certain ingredients/levels',
        'Final artwork and labelling (English or Kiswahili)',
        'Claims justification for health claims'
      ]
    },
    
    whyThisCountry: {
      title: 'Why Kenya Should Be on Your Expansion Map',
      reasons: [
        'High import dependence - Kenya relies heavily on imported finished pharmaceuticals',
        'India already exporting over US$350 million in pharma products (strong acceptance)',
        'Regional gateway - Kenyan distributors serve neighbouring EAC markets',
        'Dual burden of disease sustains demand for chronic and acute therapies',
        'Growing nutraceutical opportunity with rising overweight/obesity rates',
        'Established regulatory framework with EAC harmonisation'
      ],
      bestPortfolio: [
        'Pharma: Cardiometabolic, antibiotics/anti-infectives, paediatrics, oncology support, pain/fever, respiratory',
        'Nutra: Metabolic health, heart health, joint health, immune support, women\'s health',
        'Quality, evidence-based nutraceuticals for diet-related conditions'
      ]
    },
    
    howWeHelp: {
      title: 'How TheApostrophe Supports Your Kenya Launch',
      services: [
        'Market & Product Fit Assessment - Match portfolio with Kenya\'s disease profile',
        'CTD Dossier Development - Build modules aligned with PPB + EAC requirements',
        'Health Supplement Documentation - Classify products correctly for PPB guidelines',
        'Label & Artwork Alignment - Align master artwork to Kenyan labelling rules',
        'English/Kiswahili label coordination with importers',
        'Query & Lifecycle Management - Support importer with PPB queries and renewals',
        'Post-approval changes and variations support'
      ]
    },
    
    faqs: [
      {
        question: 'Which authority handles medicine and supplement registration in Kenya?',
        answer: 'The Pharmacy and Poisons Board (PPB), under the Ministry of Health, is responsible for the evaluation and registration of medicines and health supplements in Kenya.'
      },
      {
        question: 'What dossier format does Kenya require for generic medicines?',
        answer: 'Kenya uses a CTD-based dossier format consistent with East African Community harmonisation guidelines.'
      },
      {
        question: 'Are health supplements regulated by PPB?',
        answer: 'Yes. PPB has dedicated guidelines for health supplements and borderline products, defining documentation, safety, and labelling requirements.'
      },
      {
        question: 'What languages are allowed on labels?',
        answer: 'Labels must comply with Kenyan law; English or Kiswahili are required on key information such as product name, active ingredients, strength, batch number, expiry date, storage conditions, and directions for use.'
      },
      {
        question: 'Does Kenyan approval help in other African markets?',
        answer: 'Many East African distributors use Kenya as a regulatory and commercial base for the region. While approvals are not automatically mutual, a Kenyan registration and CTD dossier aligned with EAC standards can simplify entry into Uganda, Tanzania and other neighbours when working with experienced partners.'
      }
    ]
  };

  return <CountryTemplate countryData={kenyaData} />;
};
